<script setup>
import { Splide as Slider, SplideSlide as Slide, SplideTrack as SliderTrack } from '@splidejs/vue-splide';
import { computed, ref } from 'vue';
import AppLink from '../atoms/app/AppLink.vue';
import JobCard from './card/JobCard.vue';
import { useBreakpoints } from '../../composables/breakpoint';
import TextHeadline from "../atoms/app/text/TextHeadline.vue";

const props = defineProps({
    headline: {
        type: String,
    },
    jobItems: {
        type: Array,
    },
    linkUrl: {
        type: String,
    },
    lastSeen: {
        type: Boolean,
        default: false,
    },
});

const trackingElementDetail = computed(() => (props.lastSeen ? 'Last seen jobs' : 'Similar jobs'));

const i18n = {
    prev: 'Vorherige Slide',
    next: 'Nächste Slide',
    first: 'Zur ersten Slide gehen',
    last: 'Zur letzten Slide gehen',
    slideX: 'Gehe zu slide %s',
    pageX: 'Gehe zu Seite %s',
    carousel: 'Karusell',
    slide: 'Slide',
    slideLabel: '%s von %s',
};

const gap = '1rem'; // design specification
const sliderOptions = {
    rewind: true,
    arrows: false,
    i18n,
    gap,
    focus: 'center',
};

const { isLargeScreen } = useBreakpoints();
const carousel = ref(null);
</script>

<template>
    <div class="c-moreJobsSection">
        <div class="c-moreJobsSection__headlineRow flex mb-xl gap-l">
            <TextHeadline
                level="1"
                variant="md-400"
                class="flex-grow text-l font-semibold"
            >
                {{ headline }}
            </TextHeadline>
            <AppLink
                v-if="linkUrl"
                :href="linkUrl"
                class="flex-shrink-0 mt-2xs"
                data-qa="more jobs like this"
                data-gtm-element="AP_F: Successpage"
                :data-gtm-element-detail="trackingElementDetail"
            >
                Alle anzeigen
            </AppLink>
        </div>
        <!-- Job Cards -->
        <div class="c-moreJobsSection__jobCards">
            <template v-if="isLargeScreen">
                <div
                    v-for="job in jobItems"
                    :key="job.id"
                    class="relative"
                >
                    <JobCard
                        v-track-visibility
                        :job="job"
                        data-gtm-element="AP_F: Successpage"
                        :data-gtm-element-detail="trackingElementDetail"
                    />
                </div>
            </template>
            <Slider
                v-else
                ref="carousel"
                class="text-left gap-l"
                :options="sliderOptions"
                :has-track="false"
            >
                <SliderTrack class="w-full overflow-visible">
                    <Slide
                        v-for="job in jobItems"
                        :key="job.id"
                        class="c-moreJobsSection__jobCard text-left"
                    >
                        <JobCard
                            v-track-visibility
                            :job="job"
                            data-gtm-element="AP_F: Successpage"
                            :data-gtm-element-detail="trackingElementDetail"
                        />
                    </Slide>
                </SliderTrack>
            </Slider>
        </div>
    </div>
</template>

<style lang="scss">
@use '~@karriereat/global-styles/scss/settings/breakpoint';
@use '~@karriereat/global-styles/scss/settings/spacing';
@use '../../assets/scss/dependencies/splide';

.c-moreJobsSection {
    &__jobCards {
        margin-bottom: spacing.$k-spacing--m;

        @media (min-width: breakpoint.$k-breakpoint--l) {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            margin-bottom: 0;
            gap: spacing.$k-spacing--l;
        }
    }
}
</style>
